import {
  acceptInvitation,
  getInvitation,
  InvalidInput,
  NotFound,
  type InvitationView,
  type User,
} from '@trippath/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { problemKey } from '../../lib/format';
import { SignIn } from '../sign-in/SignIn';

type State =
  | { kind: 'loading' }
  | { kind: 'gone' }
  | { kind: 'failed' }
  | { kind: 'ready'; invitation: InvitationView };

interface Props {
  /** The account signed in, if any: it joins with one click. */
  user: User | null;
  onSignedIn: (user: User) => void;
}

/**
 * An invitation opened from its link: join as the account signed in, sign in first when the
 * invited e-mail has an account, or make one for it and join.
 */
export function Invitation({ user, onSignedIn }: Props) {
  const { t, i18n } = useTranslation();
  const ids = useId();
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    getInvitation(token).then(
      (invitation) => {
        setState({ kind: 'ready', invitation });
      },
      (error: unknown) => {
        setState({ kind: error instanceof NotFound ? 'gone' : 'failed' });
      },
    );
  }, [token]);

  if (state.kind === 'loading') {
    return <p className="page quiet">{t('trips.loading')}</p>;
  }
  if (state.kind !== 'ready') {
    return (
      <div className="page sign-in">
        <p className="form-failed" role="alert">
          {t(state.kind === 'gone' ? 'invitation.gone' : 'trips.failed')}
        </p>
      </div>
    );
  }

  const { invitation } = state;
  const join = (account: { name: string; password: string; language: string } | null) => {
    setErrors({});
    setFailed(false);
    acceptInvitation(token, account).then(
      (joined) => {
        if (joined.user) onSignedIn(joined.user);
        void navigate(
          invitation.role === 'viewer' ? `/trips/${joined.trip}/view` : `/trips/${joined.trip}`,
        );
      },
      (error: unknown) => {
        if (error instanceof InvalidInput) {
          setErrors(
            Object.fromEntries(error.fields.map((f) => [f.field, t(problemKey(f.problem))])),
          );
        } else {
          setFailed(true);
        }
      },
    );
  };

  return (
    <div className="page sign-in">
      <section className="panel" aria-labelledby={`${ids}-title`}>
        <h1 id={`${ids}-title`}>
          {t('invitation.title', {
            who: invitation.invitedBy ?? t('invitation.someone'),
            trip: invitation.tripName,
          })}
        </h1>
        <p className="quiet">
          {t('invitation.as', { role: t(`account.role.${invitation.role}`) })}
        </p>
        {failed && (
          <p className="form-failed" role="alert">
            {t('trips.failed')}
          </p>
        )}
        {user ? (
          <button
            type="button"
            className="button button-primary"
            onClick={() => {
              join(null);
            }}
          >
            {t('invitation.join', { name: user.name })}
          </button>
        ) : invitation.account ? (
          <p>{t('invitation.signInFirst', { email: invitation.email })}</p>
        ) : (
          <form
            className="form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              join({ name, password, language: i18n.language });
            }}
          >
            <p>{t('invitation.newAccount', { email: invitation.email })}</p>
            <div className="field">
              <label className="field-label" htmlFor={`${ids}-name`}>
                {t('account.name')}
              </label>
              <input
                id={`${ids}-name`}
                className="text-input"
                type="text"
                autoComplete="name"
                value={name}
                aria-invalid={errors.name !== undefined}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
              {errors.name !== undefined && <p className="field-error">{errors.name}</p>}
            </div>
            <div className="field">
              <label className="field-label" htmlFor={`${ids}-password`}>
                {t('account.password')}
              </label>
              <input
                id={`${ids}-password`}
                className="text-input"
                type="password"
                autoComplete="new-password"
                value={password}
                aria-invalid={errors.password !== undefined}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
              {errors.password !== undefined && (
                <p className="field-error">{errors.password}</p>
              )}
              <p className="quiet">{t('account.passwordHint')}</p>
            </div>
            <button type="submit" className="button button-primary">
              {t('invitation.create')}
            </button>
          </form>
        )}
      </section>
      {!user && invitation.account && <SignIn first={false} onSignedIn={onSignedIn} />}
    </div>
  );
}
